import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../store/AuthContext'
import scoresService from '../../services/scoresService'
import employeesService from '../../services/employeesService'
import { Employee, MonthlyScore } from '../../services/types'
import { SearchableSelect } from '../../components/SearchableSelect'
import { ScoreCard } from '../../components/ScoreCard'
import { ScoreDetailsModal } from '../../components/ScoreDetailsModal'
import { ConfirmModal } from '../../components/ConfirmModal'

export const HistoryPage = () => {
  const toast = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Current date
  const now = new Date()
  const currentYear = now.getFullYear()
  // State for filters
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [showReversed, setShowReversed] = useState(true)

  // State for modals
  const [selectedScore, setSelectedScore] = useState<MonthlyScore | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [scoreToReverse, setScoreToReverse] = useState<MonthlyScore | null>(null)
  const [showReverseModal, setShowReverseModal] = useState(false)

  // Check if user is admin
  const isAdmin = user?.role === 'admin'
  const isViewer = user?.role === 'viewer'

  // Generate year options (current year and 2 previous)
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  // Fetch employees
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
  })

  // Find viewer's own employee
  const myEmployee = employees.find((e) => e.user_detail?.id === user?.id)

  // For viewers, always filter to their own employee
  const effectiveEmployee = isViewer ? (myEmployee?.id ?? null) : selectedEmployee

  // Fetch scores with filters
  const { data: scores = [], isLoading } = useQuery({
    queryKey: ['scores', effectiveEmployee, selectedMonth, selectedYear, showReversed],
    queryFn: () =>
      scoresService.getAll({
        employee: effectiveEmployee || undefined,
        month: selectedMonth || undefined,
        year: selectedYear,
        is_reversed: showReversed ? undefined : false,
      }),
    enabled: isViewer ? !!myEmployee : true,
  })

  // Reverse score mutation
  const reverseMutation = useMutation({
    mutationFn: (scoreId: number) =>
      scoresService.update(scoreId, { is_reversed: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scores'] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-summary'] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-chart'] })
      toast.success('Avaliação revertida com sucesso!')
      setShowReverseModal(false)
      setScoreToReverse(null)
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao reverter avaliação.'
      toast.error(message)
    },
  })

  // Handle view details
  const handleViewDetails = (score: MonthlyScore) => {
    setSelectedScore(score)
    setShowDetailsModal(true)
  }

  // Handle reverse
  const handleReverse = (score: MonthlyScore) => {
    setScoreToReverse(score)
    setShowReverseModal(true)
  }

  // Confirm reverse
  const confirmReverse = () => {
    if (scoreToReverse) {
      reverseMutation.mutate(scoreToReverse.id)
    }
  }

  // Get employee name by id
  const getEmployeeName = (employeeId: number) => {
    return employees.find((e) => e.id === employeeId)?.full_name || 'Desconhecido'
  }

  // Sort scores by date (newest first)
  const sortedScores = [...scores].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year
    return b.month - a.month
  })

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        {isViewer ? 'Minhas Avaliações' : 'Histórico de Avaliações'}
      </h1>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className={`grid grid-cols-1 gap-4 mb-4 ${isViewer ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
          {/* Employee Filter (hidden for viewers) */}
          {!isViewer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Funcionário
              </label>
              <SearchableSelect<Employee>
                options={employees}
                value={employees.find((e) => e.id === selectedEmployee) ?? null}
                onChange={(emp) => setSelectedEmployee(emp ? emp.id : null)}
                getLabel={(e) => e.full_name}
                getSubLabel={(e) => e.primary_company_detail.name}
                getId={(e) => e.id}
                placeholder="Todos os funcionários"
              />
            </div>
          )}

          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mês
            </label>
            <select
              value={selectedMonth || ''}
              onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos os meses</option>
              <option value={1}>Janeiro</option>
              <option value={2}>Fevereiro</option>
              <option value={3}>Março</option>
              <option value={4}>Abril</option>
              <option value={5}>Maio</option>
              <option value={6}>Junho</option>
              <option value={7}>Julho</option>
              <option value={8}>Agosto</option>
              <option value={9}>Setembro</option>
              <option value={10}>Outubro</option>
              <option value={11}>Novembro</option>
              <option value={12}>Dezembro</option>
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ano
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Show Reversed Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Exibir
            </label>
            <div className="flex items-center h-[42px]">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showReversed}
                  onChange={(e) => setShowReversed(e.target.checked)}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Incluir revertidas
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
          <span>
            <strong className="text-gray-900 dark:text-white">{scores.length}</strong> avaliações encontradas
          </span>
          {scores.filter((s) => s.is_reversed).length > 0 && (
            <span>
              <strong className="text-danger">{scores.filter((s) => s.is_reversed).length}</strong> revertidas
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando avaliações...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && scores.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Nenhuma avaliação encontrada
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Tente ajustar os filtros ou criar uma nova avaliação.
          </p>
        </div>
      )}

      {/* Scores Grid */}
      {!isLoading && scores.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedScores.map((score) => (
            <ScoreCard
              key={score.id}
              score={score}
              employeeName={getEmployeeName(score.employee)}
              onViewDetails={handleViewDetails}
              onReverse={isAdmin ? handleReverse : undefined}
              canReverse={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Details Modal */}
      {selectedScore && (
        <ScoreDetailsModal
          score={selectedScore}
          employeeName={getEmployeeName(selectedScore.employee)}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedScore(null)
          }}
        />
      )}

      {/* Reverse Confirmation Modal */}
      <ConfirmModal
        isOpen={showReverseModal}
        onClose={() => {
          setShowReverseModal(false)
          setScoreToReverse(null)
        }}
        onConfirm={confirmReverse}
        title="Reverter Avaliação"
        description={
          <div className="space-y-3">
            <p>
              Tem certeza que deseja reverter esta avaliação?
            </p>
            <p className="text-sm">
              <strong>Consequências:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Os pontos desta avaliação serão zerados</li>
              <li>A avaliação será marcada como "REVERTIDO"</li>
              <li>Esta ação será registrada no log de auditoria</li>
              <li>A avaliação permanecerá no histórico para referência</li>
            </ul>
          </div>
        }
        confirmText="Sim, Reverter"
        cancelText="Cancelar"
        isDanger={true}
        isLoading={reverseMutation.isPending}
      />
    </div>
  )
}

export default HistoryPage
